import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Home, Calendar as CalendarIcon, MessageSquare, PenLine,
    ClipboardList, ChevronRight, Loader2, User, Send, CheckCircle2,
    X as XIcon, FileText, Receipt, Download, ArrowLeft, CreditCard, Trash2,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardSubtitle, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/Alert';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
    DialogFooter, DialogClose,
} from '@/components/ui/Dialog';
import { FormField, Input } from '@/components/ui/FormField';
import { SelectField } from '@/features/clients/SelectField';
import { SignatureCanvas, type SignatureCanvasHandle } from '@/features/sessions/SignatureCanvas';
import { useAuthStore } from '@/stores/auth';
import { useClientNames } from '@/api/clients';
import { useCancelReasons } from '@/api/cancelReasons';
import { useActiveParentRelationships } from '@/api/parentRelationships';
import {
    usePortalOverview, usePortalSessions, usePortalPendingSignatures,
    usePortalSessionNote, usePortalPlans,
    usePortalInvoices, usePortalInvoice, useDownloadPortalInvoiceDocx,
    useSignSessionNote, useSignPortalPlan, useCancelSession,
    type PortalDaysFilter, type PortalSession, type PortalPendingPlan,
    type PortalPlan, type PortalOverview, type PortalInvoiceSummary,
} from '@/api/portal';
import {
    usePortalMessages, useSendPortalMessage, useMarkPortalMessageRead,
} from '@/api/portalMessages';
import { useRecallPlanFromPortal } from '@/api/txPlans';
import { cn } from '@/lib/cn';
import { NoteReadOnlyView } from './NoteReadOnlyView';
import { PlanContent } from './PlanContent';
import { InvoicePaymentForm } from './InvoicePaymentForm';
import { AddCardDialog } from './AddCardDialog';
import {
    useSavedPaymentMethods, useSetDefaultPaymentMethod,
    useDeletePaymentMethod, useSetInvoiceAutopay,
    type PortalPaymentMethod,
} from '@/api/portalPayments';
import { useQueryClient } from '@tanstack/react-query';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
// ── /portal[/:clientId] ──────────────────────────────────────────────────────
// Staff-facing parent-portal preview. Dual-audience: parents with a `clientId`
// claim on their JWT auto-scope to their linked child (no picker shown); staff
// with `view_portal` pick a client first, then see the same aggregate views
// the parent would. Portal APIs are permission-aware — no client-side gating
// is required here.

type TabKey = 'overview' | 'history' | 'signatures' | 'plan' | 'messages' | 'invoices';

const BASE_TABS: { key: TabKey; label: string; icon: React.ElementType }[] = [
    { key: 'overview', label: 'Overview', icon: Home },
    { key: 'history', label: 'Session History', icon: CalendarIcon },
    { key: 'signatures', label: 'Signatures', icon: PenLine },
    { key: 'plan', label: 'Treatment Plan', icon: ClipboardList },
    { key: 'messages', label: 'Messages', icon: MessageSquare },
];

const INVOICES_TAB: { key: TabKey; label: string; icon: React.ElementType } = {
    key: 'invoices', label: 'Invoices', icon: Receipt,
};

export function ParentPortalPage() {
    const { clientId: urlClientId } = useParams<{ clientId?: string }>();
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const isParent = user?.roleType === 'parent';

    // Parents are auto-scoped server-side via JWT clientId; no picker shown.
    if (isParent) return <PortalView clientId={undefined} parentView />;

    if (!urlClientId) {
        return (
            <StaffClientPicker onPick={(id) => navigate(`/portal/${id}`)} />
        );
    }

    return <PortalView clientId={urlClientId} parentView={false} />;
}

// ── Staff client picker ─────────────────────────────────────────────────────
function StaffClientPicker({ onPick }: { onPick: (id: string) => void }) {
    const { data: clients, isLoading } = useClientNames();
    const [search, setSearch] = useState('');
    const filtered = useMemo(() => {
        const list = (clients ?? [])
            .filter((c) => c.status === 'Active')
            .slice()
            .sort((a, b) => `${a.first} ${a.last}`.localeCompare(`${b.first} ${b.last}`));
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter((c) =>
            `${c.first} ${c.last}`.toLowerCase().includes(q),
        );
    }, [clients, search]);

    return (
        <div className="p-6 max-w-[900px] mx-auto">
            <h1 className="font-display text-2xl font-bold text-text mb-1">Parent Portal</h1>
            <p className="text-xs text-text-subtle mb-5">
                Pick a client to preview what their parent/guardian sees.
            </p>

            <Card>
                <CardHeader>
                    <div>
                        <CardTitle>Clients</CardTitle>
                        <CardSubtitle>Active clients only</CardSubtitle>
                    </div>
                </CardHeader>
                <CardBody>
                    <input
                        type="search"
                        placeholder="Search by name…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full border border-border rounded-md px-3 py-1.5 text-sm bg-surface mb-3"
                    />
                    {isLoading ? (
                        <div className="py-8 flex items-center justify-center text-text-subtle text-sm">
                            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading clients…
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="py-8 text-center text-text-subtle text-sm">
                            No active clients match.
                        </div>
                    ) : (
                        <div className="divide-y divide-border border border-border rounded-md overflow-hidden">
                            {filtered.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => onPick(c.id)}
                                    className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-surface-muted transition-colors"
                                >
                                    <span className="flex items-center gap-2">
                                        <User className="w-4 h-4 text-text-subtle" />
                                        <span className="font-medium text-text">{c.first} {c.last}</span>
                                    </span>
                                    <ChevronRight className="w-4 h-4 text-text-subtle" />
                                </button>
                            ))}
                        </div>
                    )}
                </CardBody>
            </Card>
        </div>
    );
}

// ── Main view (shared for staff preview + parent) ───────────────────────────
function PortalView({
    clientId, parentView,
}: { clientId: string | undefined; parentView: boolean }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { data: overview, isLoading, error } = usePortalOverview(clientId);

    // Tab in the URL hash (#signatures, #invoices, …) so navigating into an
    // invoice detail and clicking back lands on the same tab the user left.
    const VALID_TABS: TabKey[] = ['overview', 'history', 'signatures', 'plan', 'messages', 'invoices'];
    const hashTab = location.hash.replace(/^#/, '') as TabKey;
    const tab: TabKey = VALID_TABS.includes(hashTab) ? hashTab : 'overview';
    const setTab = (next: TabKey) => navigate({ hash: `#${next}` }, { replace: true });

    if (isLoading) {
        return (
            <div className="p-6 flex items-center justify-center text-text-subtle text-sm">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading portal…
            </div>
        );
    }
    if (error || !overview) {
        return (
            <div className="p-6 max-w-[900px] mx-auto">
                <div className="p-6 text-center text-error-text bg-error-soft border border-error-border rounded-lg">
                    Unable to load the portal right now.
                </div>
            </div>
        );
    }

    const invoicesEnabled = overview.features?.portalInvoicesEnabled !== false;
    const paymentsEnabled = overview.features?.portalPaymentsEnabled !== false;
    const tabs = invoicesEnabled ? [...BASE_TABS, INVOICES_TAB] : BASE_TABS;

    return (
        <div className="p-6 max-w-[1100px] mx-auto">
            <div className="flex items-center justify-between mb-5">
                <div>
                    {!parentView && (
                        <div className="flex items-center gap-2 text-xs text-text-subtle mb-0.5">
                            <button
                                type="button"
                                className="hover:underline"
                                onClick={() => navigate('/portal')}
                            >
                                Parent Portal
                            </button>
                            <ChevronRight className="w-3 h-3" />
                            <span>{overview.client.first} {overview.client.last}</span>
                        </div>
                    )}
                    <h1 className="font-display text-2xl font-bold text-text">
                        {parentView
                            ? `Welcome, ${overview.client.first}'s family`
                            : `${overview.client.first} ${overview.client.last}`}
                    </h1>
                </div>
                {!parentView && (
                    <button
                        type="button"
                        onClick={() => navigate('/portal')}
                        className="text-xs text-text-muted hover:text-text underline-offset-2 hover:underline"
                    >
                        Change client
                    </button>
                )}
            </div>

            {/* On phones the labels would wrap, so icons+count carry the tabs
                and labels appear from `sm:` up. Scrollbar is hidden — we keep
                overflow-x-auto as a fallback on very narrow viewports. */}
            <div
                className="flex gap-1 border-b border-border mb-5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                role="tablist"
                aria-label="Portal sections"
            >
                {tabs.map((t) => {
                    const active = tab === t.key;
                    const Icon = t.icon;
                    const badge = badgeForTab(t.key, overview);
                    return (
                        <button
                            key={t.key}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => setTab(t.key)}
                            className={cn(
                                'px-3 sm:px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 whitespace-nowrap',
                                active
                                    ? 'text-text border-accent'
                                    : 'text-text-muted border-transparent hover:text-text',
                            )}
                        >
                            <Icon className="w-4 h-4" />
                            <span className="hidden sm:inline">{t.label}</span>
                            {badge > 0 && (
                                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-accent text-[#111]">
                                    {badge}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {tab === 'overview' && <OverviewSection clientId={clientId} parentView={parentView} />}
            {tab === 'history' && <HistorySection clientId={clientId} parentView={parentView} />}
            {tab === 'signatures' && <SignaturesSection clientId={clientId} parentView={parentView} />}
            {tab === 'plan' && <PlanSection clientId={clientId} parentView={parentView} />}
            {tab === 'messages' && (
                <MessagesSection
                    clientId={clientId ?? overview.client.id}
                    parentView={parentView}
                />
            )}
            {tab === 'invoices' && invoicesEnabled && (
                <InvoicesSection
                    clientId={clientId}
                    parentView={parentView}
                    paymentsEnabled={paymentsEnabled}
                />
            )}
        </div>
    );
}

function badgeForTab(key: TabKey, overview: PortalOverview): number {
    if (key === 'signatures') {
        return overview.pendingSignatures.notes + overview.pendingSignatures.plans;
    }
    if (key === 'messages') return overview.unreadMessages;
    if (key === 'invoices') return overview.unpaidInvoices;
    return 0;
}

// ── Overview section ────────────────────────────────────────────────────────
function OverviewSection({
    clientId, parentView,
}: { clientId: string | undefined; parentView: boolean }) {
    const { data, isLoading } = usePortalOverview(clientId);
    const [cancelTarget, setCancelTarget] = useState<PortalSession | null>(null);
    if (isLoading || !data) return <SectionLoader />;

    return (
        <div className="space-y-5">
            <Card>
                <CardHeader>
                    <div>
                        <CardTitle>Upcoming Sessions</CardTitle>
                        <CardSubtitle>Next few scheduled visits</CardSubtitle>
                    </div>
                </CardHeader>
                <CardBody>
                    {data.upcomingSessions.length === 0 ? (
                        <div className="py-6 text-center text-text-subtle text-sm">
                            {parentView
                                ? 'Nothing on the calendar yet. Your team will add sessions here when they\'re scheduled.'
                                : 'No upcoming sessions on the calendar.'}
                        </div>
                    ) : (
                        <ul className="divide-y divide-border">
                            {data.upcomingSessions.map((s) => (
                                <li key={s.id} className="py-2 flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-text">
                                            {formatDateLong(s.date)} · {formatTime(s.startH, s.startM)}
                                        </div>
                                        <div className="text-xs text-text-subtle truncate">
                                            {s.label || 'Session'}
                                            {s.staffName ? ` · ${s.staffName}` : ''}
                                            {s.location ? ` · ${s.location}` : ''}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <StatusBadge status={s.status} />
                                        {parentView && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setCancelTarget(s)}
                                            >
                                                <XIcon className="w-3.5 h-3.5" />
                                                Cancel session
                                            </Button>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </CardBody>
            </Card>

            {cancelTarget && (
                <CancelSessionDialog
                    session={cancelTarget}
                    onClose={() => setCancelTarget(null)}
                />
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <Card>
                    <div className="text-xs text-text-subtle">Pending signatures</div>
                    <div className="mt-1 font-display text-2xl font-bold text-text">
                        {data.pendingSignatures.notes + data.pendingSignatures.plans}
                    </div>
                    <div className="mt-0.5 text-[11px] text-text-muted">
                        {data.pendingSignatures.notes} note{data.pendingSignatures.notes === 1 ? '' : 's'} ·
                        {' '}{data.pendingSignatures.plans} plan{data.pendingSignatures.plans === 1 ? '' : 's'}
                    </div>
                </Card>
                <Card>
                    <div className="text-xs text-text-subtle">Unread messages</div>
                    <div className="mt-1 font-display text-2xl font-bold text-text">
                        {data.unreadMessages}
                    </div>
                    <div className="mt-0.5 text-[11px] text-text-muted">
                        From the practice to you
                    </div>
                </Card>
                <Card>
                    <div className="text-xs text-text-subtle">
                        {data.activePlan?.status === 'Signed' ? 'Signed treatment plan' : 'Active treatment plan'}
                    </div>
                    <div className="mt-1 text-sm font-medium text-text truncate">
                        {data.activePlan?.title ?? '—'}
                    </div>
                    <div className="mt-0.5 text-[11px] text-text-muted">
                        {data.activePlan?.signedAt
                            ? `Signed ${formatDateLong(data.activePlan.signedAt.slice(0, 10))}`
                            : 'No current plan'}
                    </div>
                </Card>
            </div>
        </div>
    );
}

// ── History section ─────────────────────────────────────────────────────────
function HistorySection({
    clientId, parentView,
}: { clientId: string | undefined; parentView: boolean }) {
    const [days, setDays] = useState<PortalDaysFilter>(7);
    const { data, isLoading } = usePortalSessions(days, clientId);
    const [viewNote, setViewNote] = useState<PortalSession | null>(null);

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="text-sm text-text">Session history</div>
                <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
                    {(['7', '30', 'all'] as const).map((k) => {
                        const active = String(days) === k;
                        return (
                            <button
                                key={k}
                                type="button"
                                onClick={() => setDays(k === 'all' ? 'all' : (Number(k) as 7 | 30))}
                                className={cn(
                                    'px-3 py-1.5 transition-colors',
                                    active ? 'bg-accent text-[#111] font-medium' : 'bg-surface hover:bg-surface-muted text-text',
                                )}
                            >
                                {k === 'all' ? 'All' : `${k}d`}
                            </button>
                        );
                    })}
                </div>
            </div>

            <Card>
                {isLoading ? (
                    <SectionLoader />
                ) : !data || data.length === 0 ? (
                    <div className="py-6 text-center text-text-subtle text-sm">
                        {parentView
                            ? 'No completed sessions in this window yet.'
                            : 'No sessions in this window.'}
                    </div>
                ) : (
                    <ul className="divide-y divide-border">
                        {data.map((s) => (
                            <HistoryRow
                                key={s.id}
                                s={s}
                                parentView={parentView}
                                onViewNote={parentView ? () => setViewNote(s) : undefined}
                            />
                        ))}
                    </ul>
                )}
            </Card>

            {viewNote && (
                <ViewNoteDialog
                    session={viewNote}
                    clientId={clientId}
                    onClose={() => setViewNote(null)}
                />
            )}
        </div>
    );
}

function HistoryRow({
    s, parentView, onViewNote,
}: {
    s: PortalSession;
    parentView: boolean;
    onViewNote?: () => void;
}) {
    // Parent view: offer a read-only note view once the note is finalized or
    // already complete (parent-signed). Submitted notes flow through the
    // Signatures tab instead.
    const canViewNote = parentView && onViewNote &&
        (s.noteStatus === 'Finalized' || s.noteStatus === 'Complete');

    return (
        <li className="py-2.5 flex items-center justify-between gap-3">
            <div className="min-w-0">
                <div className="text-sm font-medium text-text">
                    {formatDateLong(s.date)} · {formatTime(s.startH, s.startM)}
                </div>
                <div className="text-xs text-text-subtle truncate">
                    {s.label || 'Session'}
                    {s.staffName ? ` · ${s.staffName}` : ''}
                    {s.cancelledBy === 'parent' ? ' · cancelled by parent' : ''}
                </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
                <StatusBadge status={s.status} />
                {!parentView && s.noteStatus && <StatusBadge status={s.noteStatus} />}
                {s.parentSignedAt && (
                    <span
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-success-text"
                        title={`Signed ${s.parentSignedAt}`}
                    >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Signed
                    </span>
                )}
                {canViewNote && (
                    <Button variant="ghost" size="sm" onClick={onViewNote}>
                        <FileText className="w-3.5 h-3.5" /> View note
                    </Button>
                )}
            </div>
        </li>
    );
}

// ── Signatures section ──────────────────────────────────────────────────────
function SignaturesSection({
    clientId, parentView,
}: { clientId: string | undefined; parentView: boolean }) {
    const { data, isLoading } = usePortalPendingSignatures(clientId);
    const recall = useRecallPlanFromPortal(clientId ?? '');

    const [planTarget, setPlanTarget] = useState<PortalPendingPlan | null>(null);
    const [noteTarget, setNoteTarget] = useState<PortalSession | null>(null);

    if (isLoading || !data) return <SectionLoader />;

    const nothingPending = data.notes.length === 0 && data.plans.length === 0;
    return (
        <div className="space-y-5">
            {nothingPending && (
                <Card>
                    <div className="py-6 text-center text-text-subtle text-sm">
                        {parentView
                            ? 'Nothing needs your signature right now. We\'ll let you know when it does.'
                            : 'Nothing is waiting for a signature.'}
                    </div>
                </Card>
            )}

            {data.plans.length > 0 && (
                <Card>
                    <CardHeader>
                        <div>
                            <CardTitle>Treatment plans awaiting signature</CardTitle>
                            <CardSubtitle>
                                Plans the BCBA has sent to the portal for family review.
                            </CardSubtitle>
                        </div>
                    </CardHeader>
                    <CardBody>
                        <ul className="divide-y divide-border">
                            {data.plans.map((p) => (
                                <PlanPendingRow
                                    key={p.id}
                                    p={p}
                                    parentView={parentView}
                                    onRecall={() => recall.mutate(p.id)}
                                    onSign={() => setPlanTarget(p)}
                                    recalling={recall.isPending}
                                />
                            ))}
                        </ul>
                    </CardBody>
                </Card>
            )}

            {data.notes.length > 0 && (
                <Card>
                    <CardHeader>
                        <div>
                            <CardTitle>Session notes awaiting signature</CardTitle>
                            <CardSubtitle>
                                Notes your clinician has submitted for your sign-off.
                            </CardSubtitle>
                        </div>
                    </CardHeader>
                    <CardBody>
                        <ul className="divide-y divide-border">
                            {data.notes.map((n) => (
                                <li key={n.id} className="py-2.5 flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-text">
                                            {formatDateLong(n.date)} · {formatTime(n.startH, n.startM)}
                                        </div>
                                        <div className="text-xs text-text-subtle truncate">
                                            {n.label || 'Session note'}
                                            {n.staffName ? ` · ${n.staffName}` : ''}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <StatusBadge status="Submitted" />
                                        {parentView && (
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={() => setNoteTarget(n)}
                                            >
                                                <PenLine className="w-3.5 h-3.5" />
                                                Review & sign
                                            </Button>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </CardBody>
                </Card>
            )}

            {planTarget && (
                <SignPlanDialog
                    plan={planTarget}
                    clientId={clientId}
                    onClose={() => setPlanTarget(null)}
                />
            )}
            {noteTarget && (
                <SignNoteDialog
                    session={noteTarget}
                    clientId={clientId}
                    onClose={() => setNoteTarget(null)}
                />
            )}
        </div>
    );
}

function PlanPendingRow({
    p, parentView, onRecall, onSign, recalling,
}: {
    p: PortalPendingPlan;
    parentView: boolean;
    onRecall: () => void;
    onSign: () => void;
    recalling: boolean;
}) {
    return (
        <li className="py-2.5 flex items-center justify-between gap-3">
            <div className="min-w-0">
                <div className="text-sm font-medium text-text truncate">{p.title}</div>
                <div className="text-xs text-text-subtle">
                    Sent {formatDateLong(p.sentToPortalAt?.slice(0, 10) ?? null)}
                    {p.bcbaSignedName ? ` · BCBA: ${p.bcbaSignedName}` : ''}
                </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <StatusBadge status="Draft" />
                {parentView ? (
                    <Button variant="primary" size="sm" onClick={onSign}>
                        <PenLine className="w-3.5 h-3.5" />
                        Review & sign
                    </Button>
                ) : (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onRecall}
                        disabled={recalling}
                    >
                        Recall
                    </Button>
                )}
            </div>
        </li>
    );
}

// ── Plan section ────────────────────────────────────────────────────────────
function PlanSection({
    clientId, parentView,
}: { clientId: string | undefined; parentView: boolean }) {
    const { data, isLoading } = usePortalPlans(clientId);
    if (isLoading || !data) return <SectionLoader />;

    if (data.length === 0) {
        return (
            <Card>
                <div className="py-6 text-center text-text-subtle text-sm">
                    {parentView
                        ? 'Your treatment plan will appear here once your BCBA shares it.'
                        : 'No treatment plan to display yet.'}
                </div>
            </Card>
        );
    }

    return (
        <div className="space-y-5">
            {data.map((plan) => <PlanCard key={plan.id} plan={plan} />)}
        </div>
    );
}

function PlanCard({ plan }: { plan: PortalPlan }) {
    return (
        <Card>
            <CardHeader>
                <div className="min-w-0">
                    <CardTitle>
                        {plan.title}
                    </CardTitle>
                    <CardSubtitle>
                        {plan.bcbaName ? `BCBA: ${plan.bcbaName} · ` : ''}
                        {plan.startDate ? `Starts ${formatDateLong(plan.startDate)}` : 'No start date'}
                        {plan.reviewDate ? ` · Review ${formatDateLong(plan.reviewDate)}` : ''}
                    </CardSubtitle>
                </div>
                <StatusBadge status={plan.status} />
            </CardHeader>
            <CardBody>
                <div className="mb-4">
                    <PlanContent
                        notes={plan.notes}
                        behaviors={plan.behaviors}
                        programs={plan.programs}
                        customFields={plan.customFields}
                    />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-border">
                    <SignatureBlock
                        label="BCBA"
                        signedAt={plan.signedAtBcba}
                        signerName={plan.signedNameBcba}
                    />
                    <SignatureBlock
                        label="Parent / Guardian"
                        signedAt={plan.signedAtParent}
                        signerName={plan.signedNameParent}
                        relationship={plan.signedRelationshipParent}
                        pendingLabel={plan.status === 'Draft' && plan.sentToPortalAt
                            ? 'Awaiting your signature'
                            : 'Not yet signed'}
                    />
                </div>
            </CardBody>
        </Card>
    );
}

function SignatureBlock({
    label, signedAt, signerName, relationship, pendingLabel,
}: {
    label: string;
    signedAt: string | null;
    signerName: string;
    relationship?: string;
    pendingLabel?: string;
}) {
    if (signedAt) {
        return (
            <div className="text-xs">
                <div className="text-text-subtle mb-0.5">{label}</div>
                <div className="text-sm font-medium text-text flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-success-text" />
                    {signerName || '—'}
                </div>
                <div className="text-[11px] text-text-muted">
                    Signed {formatDateLong(signedAt.slice(0, 10))}
                    {relationship ? ` · ${relationship}` : ''}
                </div>
            </div>
        );
    }
    return (
        <div className="text-xs">
            <div className="text-text-subtle mb-0.5">{label}</div>
            <div className="text-sm text-text-muted italic">
                {pendingLabel ?? 'Not yet signed'}
            </div>
        </div>
    );
}

// ── Messages section ────────────────────────────────────────────────────────
function MessagesSection({
    clientId, parentView,
}: { clientId: string; parentView: boolean }) {
    const { data, isLoading } = usePortalMessages();
    const send = useSendPortalMessage();
    const markRead = useMarkPortalMessageRead();
    const [text, setText] = useState('');

    const thread = data?.[clientId] ?? [];

    const handleSend = () => {
        const body = text.trim();
        if (!body) return;
        send.mutate(
            {
                clientId,
                text: body,
                direction: parentView ? 'parent_to_practice' : 'practice_to_parent',
            },
            { onSuccess: () => setText('') },
        );
    };

    // Mark unread messages from the other side as read once per id. Ref guards
    // against the mutation feedback loop — the cache invalidates after each
    // mark-read, which would otherwise re-enter this effect with the same rows.
    const markedRef = useRef(new Set<string>());
    useEffect(() => {
        for (const m of thread) {
            if (m.read || markedRef.current.has(m.id)) continue;
            const incoming = parentView ? m.from === 'staff' : m.from === 'parent';
            if (!incoming) continue;
            markedRef.current.add(m.id);
            markRead.mutate({ id: m.id, read: true });
        }
    }, [thread, parentView, markRead]);

    if (isLoading) return <SectionLoader />;

    return (
        <Card>
            <CardHeader>
                <div>
                    <CardTitle>Messages</CardTitle>
                    <CardSubtitle>
                        {parentView
                            ? 'Chat with your clinical team.'
                            : 'Thread between the practice and the family.'}
                    </CardSubtitle>
                </div>
            </CardHeader>
            <CardBody>
                <div className="max-h-[460px] overflow-y-auto space-y-2 mb-3 pr-1">
                    {thread.length === 0 ? (
                        <div className="py-6 text-center text-text-subtle text-sm">
                            {parentView
                                ? 'No messages yet. Say hi — your team will get it on their next check.'
                                : 'No messages yet.'}
                        </div>
                    ) : (
                        thread.map((m) => {
                            const own = parentView ? m.from === 'parent' : m.from === 'staff';
                            return (
                                <div
                                    key={m.id}
                                    className={cn('flex', own ? 'justify-end' : 'justify-start')}
                                >
                                    <div
                                        className={cn(
                                            'max-w-[80%] px-3 py-2 rounded-lg text-sm',
                                            own
                                                ? 'bg-accent text-[#111]'
                                                : 'bg-surface-muted text-text border border-border',
                                        )}
                                    >
                                        <div className="text-[11px] text-text-muted mb-0.5">
                                            {m.sender || (m.from === 'staff' ? 'Practice' : 'Family')} · {m.time}
                                        </div>
                                        <div className="whitespace-pre-wrap">{m.text}</div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
                <div className="flex items-end gap-2 pt-3 border-t border-border">
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        rows={2}
                        placeholder="Type a message…"
                        className="flex-1 border border-border rounded-md px-3 py-2 text-sm bg-surface resize-none"
                    />
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleSend}
                        disabled={!text.trim() || send.isPending}
                    >
                        <Send className="w-4 h-4" /> Send
                    </Button>
                </div>
            </CardBody>
        </Card>
    );
}

// ── Invoices section ────────────────────────────────────────────────────────
type InvoiceFilter = 'open' | 'paid' | 'all';

function InvoicesSection({
    clientId, parentView, paymentsEnabled,
}: { clientId: string | undefined; parentView: boolean; paymentsEnabled: boolean }) {
    const [selectedId, setSelectedId] = useState<string | null>(null);
    // Default to "Open" so the page surfaces invoices the parent still
    // needs to act on. Filter is local React state — it persists across
    // a detail navigation because clicking a row only flips selectedId
    // (no router push), so the filter is preserved on Back automatically.
    const [filter, setFilter] = useState<InvoiceFilter>('open');
    const { data, isLoading } = usePortalInvoices(clientId, selectedId === null);

    if (selectedId) {
        return (
            <InvoiceDetailView
                id={selectedId}
                clientId={clientId}
                parentView={parentView}
                paymentsEnabled={paymentsEnabled}
                onBack={() => setSelectedId(null)}
            />
        );
    }

    if (isLoading) return <SectionLoader />;

    const invoices = data ?? [];
    const filtered = invoices.filter((inv) => {
        if (filter === 'all') return true;
        if (filter === 'paid') return inv.status === 'Paid';
        // 'open' — anything with a balance owing.
        return inv.status === 'Issued' || inv.status === 'Partial';
    });

    const FILTER_LABEL: Record<InvoiceFilter, string> = {
        open: 'Open',
        paid: 'Paid',
        all: 'All',
    };

    if (invoices.length === 0) {
        return (
            <Card>
                <div className="py-6 text-center text-text-subtle text-sm">
                    {parentView
                        ? 'No invoices yet. Anything you owe will appear here.'
                        : 'No invoices for this client.'}
                </div>
            </Card>
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="text-sm text-text">
                    {parentView ? 'Your statements' : 'Statements issued to the family'}
                </div>
                <div
                    className="inline-flex rounded-md border border-border overflow-hidden text-xs"
                    role="group"
                    aria-label="Filter invoices"
                >
                    {(['open', 'paid', 'all'] as const).map((k) => {
                        const active = filter === k;
                        return (
                            <button
                                key={k}
                                type="button"
                                aria-pressed={active}
                                onClick={() => setFilter(k)}
                                className={cn(
                                    'px-3 py-1.5 transition-colors',
                                    active
                                        ? 'bg-accent text-[#111] font-medium'
                                        : 'bg-surface hover:bg-surface-muted text-text',
                                )}
                            >
                                {FILTER_LABEL[k]}
                            </button>
                        );
                    })}
                </div>
            </div>

            <Card>
                <CardBody>
                    {filtered.length === 0 ? (
                        <div className="py-6 text-center text-text-subtle text-sm">
                            {filter === 'open'
                                ? (parentView
                                    ? 'No open invoices — you\'re all caught up.'
                                    : 'No open invoices for this client.')
                                : filter === 'paid'
                                    ? 'No paid invoices yet.'
                                    : 'No invoices to show.'}
                        </div>
                    ) : (
                        <ul className="divide-y divide-border">
                            {filtered.map((inv) => (
                                <InvoiceRow
                                    key={inv.id}
                                    inv={inv}
                                    onOpen={() => setSelectedId(inv.id)}
                                />
                            ))}
                        </ul>
                    )}
                </CardBody>
            </Card>

            {/*
              Phase 8: parent-only saved-cards manager. Hidden from the
              staff preview (parents own their card data; staff have no
              business listing it).
            */}
            <SavedCardsSection enabled={parentView && paymentsEnabled} />
        </div>
    );
}

function InvoiceRow({
    inv, onOpen,
}: { inv: PortalInvoiceSummary; onOpen: () => void }) {
    return (
        <li>
            <button
                type="button"
                onClick={onOpen}
                className="w-full py-2.5 flex items-center justify-between gap-3 text-left hover:bg-surface-muted transition-colors -mx-3 px-3 rounded-md"
            >
                <div className="min-w-0">
                    <div className="text-sm font-medium text-text">
                        {inv.displayId}
                    </div>
                    <div className="text-xs text-text-subtle">
                        Issued {formatDateShort(inv.issuedAt?.slice(0, 10) ?? null)}
                        {inv.dueDate ? ` · Due ${formatDateShort(inv.dueDate)}` : ''}
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                        <div className="text-sm font-medium text-text">
                            {formatMoney(inv.balance)}
                        </div>
                        <div className="text-[11px] text-text-muted">
                            {inv.status === 'Paid' ? 'Paid in full' : `of ${formatMoney(inv.subtotal)}`}
                        </div>
                    </div>
                    <StatusBadge status={inv.status} />
                    <ChevronRight className="w-4 h-4 text-text-subtle" />
                </div>
            </button>
        </li>
    );
}

function InvoiceDetailView({
    id, clientId, parentView, paymentsEnabled, onBack,
}: {
    id: string;
    clientId: string | undefined;
    parentView: boolean;
    paymentsEnabled: boolean;
    onBack: () => void;
}) {
    // Post-submit pay state machine — shared by both the saved-card
    // path (server inserts the row as 'processing' and we poll for the
    // webhook to flip the invoice) AND the new-card path (Stripe
    // confirmPayment resolves client-side but the invoice doesn't roll
    // forward until our /confirm route + webhook apply the payment).
    // Both paths set `paySubmittedAt + initialBalanceAtSubmit` on
    // success so the Step 1/2 cards hide and the banner appears in
    // place of the form. Without this, the new-card form would remount
    // (key={amountCentsToPay} re-keys when the refetched balance
    // changes amountInput) and the parent would see a fresh blank
    // form rather than confirmation.
    //   - On submit: capture the balance the parent was paying against.
    //   - Poll the invoice query for ~30s.
    //   - If inv.balance drops below the captured value, the payment
    //     was applied — switch to confirmed.
    //   - If the window times out, show "still processing" honestly
    //     instead of false success.
    // Defined before usePortalInvoice so the pollMs option is in scope
    // for the query hook below.
    const [savedPaySubmitting, setSavedPaySubmitting] = useState(false);
    const [savedPayError, setSavedPayError] = useState<string | null>(null);
    const [paySubmittedAt, setPaySubmittedAt] = useState<number | null>(null);
    const [paySubmittedVia, setPaySubmittedVia] = useState<'saved' | 'new' | null>(null);
    // True when the new-card path submitted an ACH or other delayed-
    // settlement payment (PaymentIntent.status === 'processing' on
    // confirmPayment). We skip the spinner/polling banner in that
    // case and show a settle-in-3-5-days banner instead — the
    // pendingSettlementCents banner takes over once the webhook
    // lands. Saved-card path leaves this false because we don't
    // have the Stripe response client-side.
    const [pendingSettlement, setPendingSettlement] = useState(false);
    const [initialBalanceAtSubmit, setInitialBalanceAtSubmit] = useState<number | null>(null);
    const [confirmTimedOut, setConfirmTimedOut] = useState(false);
    const awaitingConfirm = paySubmittedAt !== null && !confirmTimedOut && !pendingSettlement;

    const { data: inv, isLoading, error } = usePortalInvoice(id, clientId, {
        // Poll every 2s while waiting for the webhook to flip the
        // invoice; stop when confirmed (next render-after-fetch) or
        // when the timeout fires.
        refetchInterval: awaitingConfirm ? 2_000 : false,
    });
    const download = useDownloadPortalInvoiceDocx();
    const qc = useQueryClient();
    // Phase 8 follow-up — two-step pay flow. Step 1 collects the
    // amount; Step 2 picks the payment method. methodChoice defaults
    // to 'saved' when a default method exists, else 'new'.
    const [methodChoice, setMethodChoice] = useState<'saved' | 'new'>('saved');
    const savedMethodsQuery = useSavedPaymentMethods(parentView && paymentsEnabled);
    const autopayMutation = useSetInvoiceAutopay();
    const defaultMethod = (savedMethodsQuery.data || []).find((m) => m.isDefault) || null;

    // 30s polling-window timer for the confirming state.
    useEffect(() => {
        if (paySubmittedAt === null) return;
        setConfirmTimedOut(false);
        const t = setTimeout(() => setConfirmTimedOut(true), 30_000);
        return () => clearTimeout(t);
    }, [paySubmittedAt]);

    // Confirmed when invoice balance has dropped below what the
    // parent was paying against. Derived (no extra state) so it
    // reacts to the polled invoice refresh.
    const payConfirmed =
        paySubmittedAt !== null &&
        initialBalanceAtSubmit !== null &&
        inv != null &&
        inv.balance < initialBalanceAtSubmit;
    // Snap methodChoice to whichever option is actually available the
    // first time the saved-methods query resolves. Without this the
    // parent could land on Step 2 with the "saved" option selected
    // when no saved method exists, or vice versa.
    useEffect(() => {
        if (!savedMethodsQuery.data) return;
        if (defaultMethod && methodChoice === 'new') {
            // Allow either; do NOT auto-flip if the parent already
            // picked 'new' deliberately. Initial render handles the
            // default-selection below.
            return;
        }
        if (!defaultMethod && methodChoice === 'saved') {
            setMethodChoice('new');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedMethodsQuery.data, defaultMethod]);
    // Parent-typed partial amount. Stored in dollars so the text input
    // round-trips cleanly; converted to cents on submit. Min $10 (matches
    // the server's MIN_PARTIAL_PAYMENT_CENTS floor; allowing a smaller
    // payment only when it's the entire remaining balance — server is
    // authoritative on that branch).
    //
    // MUST live above the loading / error early returns so hook order
    // stays stable across the (no-inv → inv loaded) transition — React
    // throws "Rendered more hooks than during the previous render" if
    // the useState only runs on the data-loaded render.
    const [amountInput, setAmountInput] = useState<string>(
        () => (inv?.balance ?? 0).toFixed(2),
    );
    useEffect(() => {
        if (inv) setAmountInput(inv.balance.toFixed(2));
    }, [inv?.balance, inv]);

    if (isLoading) return <SectionLoader />;
    if (error || !inv) {
        return (
            <div className="space-y-3">
                <Button variant="ghost" size="sm" onClick={onBack}>
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to invoices
                </Button>
                <div className="p-6 text-center text-error-text bg-error-soft border border-error-border rounded-lg">
                    Unable to load this invoice.
                </div>
            </div>
        );
    }

    const handleDownload = () => {
        download.mutate({ id: inv.id, displayId: inv.displayId, clientId });
    };

    // Parent-only payment surface. Hidden when:
    //   - the viewer is staff (parentView=false) — staff cannot pay on a parent's behalf
    //   - the practice has portalPaymentsEnabled=false (Phase 5 opt-out)
    //   - the invoice is fully paid or voided — nothing left to charge
    //   - Phase 9: in-flight ACH settlement covers the remaining balance —
    //     the parent has already submitted enough to clear the invoice +
    //     is now waiting on the bank. Showing Pay Now in that window
    //     would tempt them into paying twice.
    // Note: paidInPortalAt being set is NOT a block — partial payments
    // stamp it on each successful charge, and the parent should still
    // be able to pay the remaining balance.
    const balanceCents = Math.round(inv.balance * 100);
    const pendingSettlementCents = inv.pendingSettlementCents ?? 0;
    const pendingFullyCoversBalance = pendingSettlementCents >= balanceCents && balanceCents > 0;
    const canPay =
        parentView &&
        paymentsEnabled &&
        balanceCents > 0 &&
        inv.status !== 'Paid' &&
        !pendingFullyCoversBalance;

    // Partial-payment rule, mirrors the server
    // (apps/pma-server/src/routes/portal_payments.js):
    //   - Balance < 2 × MIN ($20): only the full balance is allowed.
    //     Any partial would either be sub-$10 itself or strand a
    //     sub-$10 leftover that Stripe's $0.50 minimum can't accept.
    //   - Balance ≥ $20: partials must sit in [$10, balance − $10],
    //     or the parent can pay the full balance.
    const PORTAL_MIN_PAYMENT_DOLLARS = 10;
    const PORTAL_MIN_PAYMENT_CENTS = PORTAL_MIN_PAYMENT_DOLLARS * 100;
    const fullPaymentOnly = balanceCents < 2 * PORTAL_MIN_PAYMENT_CENTS;
    const parsedAmount = Number(amountInput);
    const amountCentsToPay = Math.round(parsedAmount * 100);
    const isFullPayment = amountCentsToPay === balanceCents;
    const inPartialRange =
        amountCentsToPay >= PORTAL_MIN_PAYMENT_CENTS &&
        amountCentsToPay <= balanceCents - PORTAL_MIN_PAYMENT_CENTS;
    const amountValid =
        Number.isFinite(parsedAmount) &&
        parsedAmount > 0 &&
        amountCentsToPay <= balanceCents &&
        (isFullPayment || (!fullPaymentOnly && inPartialRange));
    // The smallest amount the parent is actually allowed to pay right
    // now. For full-only invoices that's the balance; otherwise $10.
    const effectiveMinCents = fullPaymentOnly ? balanceCents : PORTAL_MIN_PAYMENT_CENTS;

    const savedMethodLabel = defaultMethod
        ? (defaultMethod.brand
            ? `${defaultMethod.brand} ····${defaultMethod.last4 ?? ''}`
            : `Saved method ····${defaultMethod.last4 ?? ''}`)
        : null;

    // TypeScript loses the early-return narrowing of `inv` inside
    // function-declaration bodies (only direct uses in JSX retain it).
    // Capture the narrowed value once so the helpers below can read
    // it without non-null assertions.
    const invoice = inv;

    async function paySaved() {
        if (!defaultMethod || savedPaySubmitting || !amountValid) return;
        setSavedPaySubmitting(true);
        setSavedPayError(null);
        try {
            const res = await fetch(`/api/portal/invoices/${invoice.id}/payment-intent`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${useAuthStore.getState().token ?? ''}`,
                },
                body: JSON.stringify({
                    amount_cents: amountCentsToPay,
                    payment_method_id: defaultMethod.id,
                }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.error || `Payment failed (${res.status}).`);
            }
            // Server inserted as 'processing'. Capture the balance
            // we're paying against so the polling loop can detect
            // the webhook-driven roll-forward (inv.balance dropping
            // below this value means the payment applied).
            setInitialBalanceAtSubmit(invoice.balance);
            setPaySubmittedVia('saved');
            setPaySubmittedAt(Date.now());
            qc.invalidateQueries({ queryKey: ['portal'] });
        } catch (e: unknown) {
            setSavedPayError(e instanceof Error ? e.message : 'Payment failed.');
        } finally {
            setSavedPaySubmitting(false);
        }
    }

    function setAmountToFull() {
        setAmountInput(invoice.balance.toFixed(2));
    }
    function setAmountToMinimum() {
        // Under the new partial rule, this button is hidden when the
        // balance is < $20 (only the full balance is valid). When it
        // IS shown, the floor is always $10.
        setAmountInput((effectiveMinCents / 100).toFixed(2));
    }

    return (
        <div className="space-y-4">
            {/* Toolbar */}
            <div className="flex items-center justify-between gap-3">
                <Button variant="ghost" size="sm" onClick={onBack}>
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to invoices
                </Button>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={handleDownload}
                    disabled={download.isPending}
                >
                    <Download className="w-3.5 h-3.5" />
                    {download.isPending ? 'Downloading…' : 'Download .docx'}
                </Button>
            </div>

            {/*
              Phase 9 of pma-stripe-payments — ACH settlement-pending
              banner. ACH charges sit in 'processing' for 3-5 business
              days before the bank settles; without this affordance the
              invoice still reads as Issued / Partial with a full
              balance, and parents email the practice asking "did my
              payment go through?". Banner stays mounted whether or not
              the Pay Now form is hidden — when pending covers the
              balance we suppress Pay Now too (gated above), so the
              banner becomes the single source of truth on the page.
            */}
            {pendingSettlementCents > 0 && inv.status !== 'Paid' && (
                <Alert variant="info" data-testid="portal-invoice-pending-settlement">
                    <div className="space-y-1">
                        <div className="font-medium">
                            Payment pending settlement
                        </div>
                        <div className="text-sm">
                            {formatMoney(pendingSettlementCents / 100)} from your bank account
                            is being processed. Funds typically arrive in 3–5 business days,
                            after which this invoice will update automatically.
                        </div>
                    </div>
                </Alert>
            )}

            {/* Step 1: Enter amount */}
            {canPay && paySubmittedAt === null && (
                <Card>
                    <CardHeader>
                        <CardTitle>Step 1: Enter amount to pay</CardTitle>
                        <CardSubtitle>
                            Outstanding balance: {formatMoney(inv.balance)}.
                        </CardSubtitle>
                    </CardHeader>
                    <CardBody>
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm text-text-muted">$</span>
                            <input
                                id="portal-pay-amount"
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                min={effectiveMinCents / 100}
                                max={inv.balance}
                                value={amountInput}
                                onChange={(e) => setAmountInput(e.target.value)}
                                aria-invalid={!amountValid}
                                aria-describedby={!amountValid ? 'portal-pay-amount-hint' : undefined}
                                className="w-32 text-sm border border-border rounded-md px-2 py-1 bg-surface text-right tabular-nums"
                            />
                            <Button
                                variant="ghost"
                                size="sm"
                                type="button"
                                onClick={setAmountToFull}
                                data-testid="portal-pay-amount-full"
                            >
                                Full ({formatMoney(inv.balance)})
                            </Button>
                            {!fullPaymentOnly && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    type="button"
                                    onClick={setAmountToMinimum}
                                    data-testid="portal-pay-amount-min"
                                >
                                    Minimum ({formatMoney(effectiveMinCents / 100)})
                                </Button>
                            )}
                        </div>
                        {!amountValid && (
                            <p
                                id="portal-pay-amount-hint"
                                className="mt-2 text-xs text-error-text"
                                role="alert"
                            >
                                {!Number.isFinite(parsedAmount) || parsedAmount <= 0
                                    ? 'Enter an amount greater than zero.'
                                    : amountCentsToPay > balanceCents
                                        ? `Cannot exceed the balance of ${formatMoney(inv.balance)}.`
                                        : fullPaymentOnly
                                            ? `Balances under $${(2 * PORTAL_MIN_PAYMENT_DOLLARS).toFixed(2)} must be paid in full (${formatMoney(inv.balance)}).`
                                            : amountCentsToPay < PORTAL_MIN_PAYMENT_CENTS
                                                ? `Partial payments must be at least $${PORTAL_MIN_PAYMENT_DOLLARS}.00.`
                                                : `Partial payments must leave at least $${PORTAL_MIN_PAYMENT_DOLLARS}.00 remaining, or you can pay the full balance (${formatMoney(inv.balance)}).`}
                            </p>
                        )}
                    </CardBody>
                </Card>
            )}

            {/* Step 2: Choose payment method */}
            {canPay && paySubmittedAt === null && (
                <Card>
                    <CardHeader>
                        <CardTitle>Step 2: Choose how to pay</CardTitle>
                        <CardSubtitle>
                            Card details go directly to Stripe — they never touch this site.
                        </CardSubtitle>
                    </CardHeader>
                    <CardBody>
                        <fieldset className="space-y-3" disabled={!amountValid}>
                            {defaultMethod && (
                                <label
                                    className={cn(
                                        'flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors',
                                        methodChoice === 'saved'
                                            ? 'border-accent bg-accent-soft'
                                            : 'border-border hover:bg-surface-muted',
                                    )}
                                    data-testid="portal-method-saved"
                                >
                                    <input
                                        type="radio"
                                        name="portal-payment-method"
                                        value="saved"
                                        checked={methodChoice === 'saved'}
                                        onChange={() => setMethodChoice('saved')}
                                        className="mt-1"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-text inline-flex items-center gap-2">
                                            <CreditCard className="w-3.5 h-3.5" />
                                            Use my saved method ({savedMethodLabel})
                                        </div>
                                        {methodChoice === 'saved' && (
                                            <div className="mt-3 space-y-2">
                                                {savedPayError && (
                                                    <Alert variant="danger">{savedPayError}</Alert>
                                                )}
                                                <Button
                                                    variant="success"
                                                    size="sm"
                                                    onClick={paySaved}
                                                    disabled={savedPaySubmitting || !amountValid}
                                                    data-testid="portal-pay-saved"
                                                >
                                                    {savedPaySubmitting
                                                        ? 'Processing…'
                                                        : `Pay ${formatMoney(parsedAmount)} with ${savedMethodLabel}`}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </label>
                            )}

                            <label
                                className={cn(
                                    'flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors',
                                    methodChoice === 'new'
                                        ? 'border-accent bg-accent-soft'
                                        : 'border-border hover:bg-surface-muted',
                                )}
                                data-testid="portal-method-new"
                            >
                                <input
                                    type="radio"
                                    name="portal-payment-method"
                                    value="new"
                                    checked={methodChoice === 'new'}
                                    onChange={() => setMethodChoice('new')}
                                    className="mt-1"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-text">
                                        {defaultMethod
                                            ? 'Use a different card or bank'
                                            : 'Pay with a new card or bank'}
                                    </div>
                                    {methodChoice === 'new' && (
                                        <div className="mt-3">
                                            <InvoicePaymentForm
                                                key={amountCentsToPay}
                                                invoiceId={inv.id}
                                                invoiceDisplayId={inv.displayId}
                                                amountCents={amountCentsToPay}
                                                allowSaveCard
                                                onCancel={() => {
                                                    // Collapse the form back; if the parent has
                                                    // a saved method, default back to it,
                                                    // otherwise stay on 'new' (it's the only
                                                    // option) but reset the form by re-keying.
                                                    if (defaultMethod) setMethodChoice('saved');
                                                }}
                                                onSucceeded={({ isDelayedSettlement }) => {
                                                    // Same state machine the saved-card path uses
                                                    // so Step 1/2 collapse and the success banner
                                                    // appears. Without this, the refetched balance
                                                    // would shift amountInput → re-key the
                                                    // PaymentInner form → wipe its local
                                                    // `succeeded` state and leave the parent
                                                    // staring at a fresh blank card form.
                                                    setInitialBalanceAtSubmit(invoice.balance);
                                                    setPaySubmittedVia('new');
                                                    setPendingSettlement(isDelayedSettlement);
                                                    setPaySubmittedAt(Date.now());
                                                    qc.invalidateQueries({ queryKey: ['portal'] });
                                                    qc.invalidateQueries({ queryKey: ['portal', 'payment-methods'] });
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </label>
                        </fieldset>
                    </CardBody>
                </Card>
            )}

            {/*
              Pay status banner — shared by both the saved-card path
              and the new-card (Elements) path. The saved-card path
              defers to the webhook entirely; the new-card path races
              ahead via the /confirm route and usually flips to
              confirmed on the first refetch.
              - Confirmed: the polled invoice query shows a lower
                balance than the parent submitted against → the
                webhook payment_intent.succeeded landed + applied
                (or the confirm-route race-ahead applied).
              - Confirming: we've sent the request, still polling,
                window not yet timed out.
              - Timed out: 30s elapsed without a balance drop. The
                payment may still land later (webhook retry, queued
                processing) but we won't claim success — the parent
                gets honest "still processing" copy with a refresh
                hint.
            */}
            {paySubmittedAt !== null && pendingSettlement && (
                // ACH / other delayed-settlement payment. Skip the
                // polling-and-spinner loop entirely — funds won't
                // arrive for 3–5 business days. The Phase 9
                // pendingSettlementCents banner above this block takes
                // over once the webhook lands; this success banner is
                // the one-shot acknowledgement that the request was
                // accepted.
                <Alert variant="success" data-testid="portal-pay-ach-submitted">
                    <div className="space-y-1">
                        <div className="font-medium">Bank transfer submitted.</div>
                        <div className="text-sm">
                            Funds typically settle in 3–5 business days. This invoice will
                            update automatically when the transfer clears. Stripe will email
                            you a receipt shortly.
                        </div>
                    </div>
                </Alert>
            )}
            {paySubmittedAt !== null && !pendingSettlement && payConfirmed && (
                <Alert variant="success" data-testid="portal-pay-confirmed">
                    Payment of {formatMoney(initialBalanceAtSubmit! - inv.balance)}
                    {paySubmittedVia === 'saved' && savedMethodLabel ? ` on ${savedMethodLabel}` : ''} confirmed.
                </Alert>
            )}
            {paySubmittedAt !== null && !pendingSettlement && !payConfirmed && !confirmTimedOut && (
                <Alert variant="info" data-testid="portal-pay-confirming">
                    <div className="flex items-start gap-2">
                        <Loader2 className="w-4 h-4 animate-spin mt-0.5 shrink-0" />
                        <div>
                            <div className="font-medium">Confirming payment with Stripe…</div>
                            <div className="text-sm">
                                Your payment was submitted. This usually takes a few seconds —
                                we'll update this invoice automatically when the bank confirms.
                            </div>
                        </div>
                    </div>
                </Alert>
            )}
            {paySubmittedAt !== null && !pendingSettlement && !payConfirmed && confirmTimedOut && (
                <Alert variant="warning" data-testid="portal-pay-timeout">
                    <div className="space-y-1">
                        <div className="font-medium">Still processing</div>
                        <div className="text-sm">
                            Stripe is taking longer than expected to confirm. The payment
                            may still complete — refresh this page in a few minutes to
                            check status, or contact the practice if it doesn't update.
                        </div>
                    </div>
                </Alert>
            )}

            {/*
              Per-invoice autopay toggle. Hidden when there's no saved
              default card; the server PATCH 409s in that case anyway,
              but hiding the affordance avoids the dead-end. Voided
              invoices never reach this view — the server's
              GET /api/portal/invoices/:id 404s on them — so the
              !== 'Paid' guard is enough.
            */}
            {parentView && paymentsEnabled && defaultMethod && inv.status !== 'Paid' && (
                <AutopayToggleRow
                    invoice={inv}
                    method={defaultMethod}
                    pending={autopayMutation.isPending}
                    onToggle={(enabled) => {
                        autopayMutation.mutate(
                            { invoiceId: inv.id, enabled },
                            { onSuccess: () => qc.invalidateQueries({ queryKey: ['portal'] }) },
                        );
                    }}
                />
            )}

            <Card>
                <CardHeader>
                    <div>
                        <CardTitle>Invoice {inv.displayId}</CardTitle>
                        <CardSubtitle>
                            Issued {formatDateShort(inv.issuedAt?.slice(0, 10) ?? null)}
                            {inv.dueDate ? ` · Due ${formatDateShort(inv.dueDate)}` : ''}
                        </CardSubtitle>
                    </div>
                    <StatusBadge status={inv.status} />
                </CardHeader>
                <CardBody>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3 pb-4 border-b border-border">
                        <SummaryStat label="Subtotal" value={formatMoney(inv.subtotal)} />
                        <SummaryStat label="Paid" value={formatMoney(inv.paidAmount)} />
                        <SummaryStat
                            label="Balance due"
                            value={formatMoney(inv.balance)}
                            emphasise={inv.balance > 0}
                        />
                    </div>

                    {inv.refundedInPortal > 0 && (
                        <div
                            className="mb-5 text-xs text-text-muted flex items-center justify-end gap-2"
                            data-testid="portal-invoice-refunded"
                        >
                            <span className="text-text-subtle uppercase tracking-wide">Refunded</span>
                            <span className="text-text font-medium tabular-nums">
                                {formatMoney(inv.refundedInPortal)}
                            </span>
                        </div>
                    )}

                    {inv.remitToName || inv.remitToAddress ? (
                        <div className="mb-5 text-xs">
                            <div className="text-text-subtle mb-0.5">Remit to</div>
                            <div className="text-text font-medium">{inv.remitToName}</div>
                            {inv.remitToAddress && (
                                <div className="text-text-muted whitespace-pre-line">
                                    {inv.remitToAddress}
                                </div>
                            )}
                        </div>
                    ) : null}

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[11px] text-text-subtle uppercase tracking-wide border-b border-border">
                                    <th className="py-2 pr-3 font-medium">Date</th>
                                    <th className="py-2 pr-3 font-medium">Service</th>
                                    <th className="py-2 pr-3 font-medium text-right">Units</th>
                                    <th className="py-2 pr-3 font-medium text-right">Rate</th>
                                    <th className="py-2 font-medium text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {inv.lines.map((ln) => (
                                    <tr key={ln.id}>
                                        <td className="py-2 pr-3 text-text">
                                            {formatDateLong(ln.dos)}
                                        </td>
                                        <td className="py-2 pr-3 text-text">
                                            <div>{ln.serviceCode || '—'}</div>
                                            <div className="text-[11px] text-text-muted">
                                                {ln.claimDisplayId}
                                                {ln.prSourceDisplayId
                                                    ? ` · from ${ln.prSourceDisplayId}`
                                                    : ''}
                                            </div>
                                        </td>
                                        <td className="py-2 pr-3 text-right text-text">
                                            {ln.units}
                                        </td>
                                        <td className="py-2 pr-3 text-right text-text">
                                            {formatMoney(ln.unitRate)}
                                        </td>
                                        <td className="py-2 text-right text-text font-medium">
                                            {formatMoney(ln.amountSnapshot)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {(inv.paymentInstructions || inv.terms || inv.footerNote) && (
                        <div className="mt-5 pt-4 border-t border-border space-y-3 text-xs">
                            {inv.paymentInstructions && (
                                <div>
                                    <div className="text-text-subtle mb-0.5">How to pay</div>
                                    <div className="text-text whitespace-pre-line">
                                        {inv.paymentInstructions}
                                    </div>
                                </div>
                            )}
                            {inv.terms && (
                                <div>
                                    <div className="text-text-subtle mb-0.5">Terms</div>
                                    <div className="text-text whitespace-pre-line">
                                        {inv.terms}
                                    </div>
                                </div>
                            )}
                            {inv.footerNote && (
                                <div className="text-text-muted italic">
                                    {inv.footerNote}
                                </div>
                            )}
                        </div>
                    )}

                    {!parentView && inv.deliveredAt && (
                        <div className="mt-4 text-[11px] text-text-muted">
                            Delivered via {inv.deliveryMethod ?? '—'} on {formatDateLong(inv.deliveredAt.slice(0, 10))}
                        </div>
                    )}
                </CardBody>
            </Card>
        </div>
    );
}

function SummaryStat({
    label, value, emphasise,
}: { label: string; value: string; emphasise?: boolean }) {
    return (
        <div>
            <div className="text-[11px] text-text-subtle uppercase tracking-wide">{label}</div>
            <div className={cn(
                'mt-0.5 font-display text-lg font-bold',
                emphasise ? 'text-text' : 'text-text',
            )}>
                {value}
            </div>
        </div>
    );
}

function formatMoney(n: number): string {
    return new Intl.NumberFormat(undefined, {
        style: 'currency', currency: 'USD',
    }).format(n);
}

// ── Phase 8: per-invoice autopay toggle row ─────────────────────────────────
function AutopayToggleRow({
    invoice, method, pending, onToggle,
}: {
    invoice: { autopayEnabled: boolean; dueDate: string | null };
    method: PortalPaymentMethod;
    pending: boolean;
    onToggle: (enabled: boolean) => void;
}) {
    const enabled = invoice.autopayEnabled;
    const label = method.brand
        ? `${method.brand} ····${method.last4 ?? ''}`
        : `Saved card ····${method.last4 ?? ''}`;
    // Phase 8 follow-up — when the parent's default card was saved
    // via the on_session path, autopay can't be enabled against it.
    // Show the row but disable the toggle and explain why.
    if (!method.autopayConsent && !enabled) {
        return (
            <div
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-border bg-surface-muted"
                data-testid="portal-autopay-row"
            >
                <div className="text-sm">
                    <div className="font-medium text-text">Autopay off</div>
                    <div className="text-xs text-text-muted">
                        {label} was saved without autopay permission. To use autopay, add it again with the autopay box ticked.
                    </div>
                </div>
                <Button variant="ghost" size="sm" disabled>
                    Not eligible
                </Button>
            </div>
        );
    }
    return (
        <div
            className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-border bg-surface-muted"
            data-testid="portal-autopay-row"
        >
            <div className="text-sm">
                <div className="font-medium text-text">
                    Autopay {enabled ? 'on' : 'off'}
                </div>
                <div className="text-xs text-text-muted">
                    {enabled
                        ? `Will charge ${label}${invoice.dueDate ? ` on or shortly before ${formatDateLong(invoice.dueDate)}` : ''} (depending on this practice's autopay window).`
                        : `Charge ${label} automatically when this invoice comes due.`}
                </div>
            </div>
            <Button
                variant={enabled ? 'ghost' : 'success'}
                size="sm"
                disabled={pending}
                onClick={() => onToggle(!enabled)}
            >
                {pending ? 'Saving…' : enabled ? 'Turn off' : 'Turn on'}
            </Button>
        </div>
    );
}

// ── Phase 8: saved-cards section (list + add + default + delete) ────────────
//
// Lives at the bottom of the Invoices tab. The per-invoice flows
// above are scoped to one invoice; this section is the parent's
// "manage my cards" surface.
function SavedCardsSection({ enabled }: { enabled: boolean }) {
    const { data: methods = [], isLoading } = useSavedPaymentMethods(enabled);
    const setDefault = useSetDefaultPaymentMethod();
    const deletePM = useDeletePaymentMethod();
    const [showAdd, setShowAdd] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Delete-confirm dialog state. Two reasons we don't just call
    // .mutate() on the Remove button:
    //   1. Removing a card is destructive — once detached, the parent
    //      has to re-enter the card to use it again. A native
    //      window.confirm is banned by the design system (no native
    //      dialogs in SPAs); the inline Dialog matches the rest of
    //      this view's affordance language.
    //   2. The server 409s if the card is the default for any autopay-
    //      enabled invoices, and returns `affectedInvoices: [{id,
    //      displayId}]` so we can name them — the previous code path
    //      flattened the response through extractErrorMessage and threw
    //      that structured list away.
    const [pendingDelete, setPendingDelete] = useState<PortalPaymentMethod | null>(null);
    const [deleteBlocked, setDeleteBlocked] = useState<{
        displayIds: string[];
    } | null>(null);

    if (!enabled) return null;

    return (
        <Card className="mt-5" data-testid="portal-saved-cards">
            <CardHeader>
                <div>
                    <CardTitle>Saved payment methods</CardTitle>
                    <CardSubtitle>
                        Save a card for one-click payments &amp; autopay. Card details are
                        held by Stripe — they never touch this site.
                    </CardSubtitle>
                </div>
                <Button variant="primary" size="sm" onClick={() => setShowAdd(true)}>
                    Add a card
                </Button>
            </CardHeader>
            <CardBody>
                {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                {isLoading ? (
                    <SectionLoader />
                ) : methods.length === 0 ? (
                    <div className="py-3 text-sm text-text-subtle">
                        You haven't saved any payment methods yet.
                    </div>
                ) : (
                    <ul className="divide-y divide-border">
                        {methods.map((m) => (
                            <li key={m.id} className="flex items-center justify-between gap-3 py-2">
                                <div className="flex items-center gap-3">
                                    <CreditCard className="w-4 h-4 text-text-muted" />
                                    <div>
                                        <div className="text-sm text-text">
                                            {m.brand ? `${m.brand} ····${m.last4 ?? ''}` : `····${m.last4 ?? ''}`}
                                            {m.expMonth && m.expYear ? (
                                                <span className="text-xs text-text-subtle ml-2">
                                                    Exp {String(m.expMonth).padStart(2, '0')}/{String(m.expYear).slice(-2)}
                                                </span>
                                            ) : null}
                                        </div>
                                        <div className="flex items-center gap-2 text-xs">
                                            {m.isDefault && (
                                                <span className="text-success-text">Default</span>
                                            )}
                                            {/*
                                              Phase 8 follow-up — autopay-eligible
                                              indicator. Cards saved without
                                              autopay consent show "Save-only"
                                              so the parent knows why the per-
                                              invoice autopay toggle is locked
                                              for this card.
                                            */}
                                            {m.autopayConsent ? (
                                                <span className="text-text-subtle">Autopay-eligible</span>
                                            ) : (
                                                <span className="text-text-subtle">Save-only (no autopay)</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {!m.isDefault && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            disabled={setDefault.isPending}
                                            onClick={() => {
                                                setError(null);
                                                setDefault.mutate(m.id, {
                                                    onError: (err) => setError(extractErrorMessage(err, 'Could not set default.')),
                                                });
                                            }}
                                        >
                                            Make default
                                        </Button>
                                    )}
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        disabled={deletePM.isPending}
                                        onClick={() => {
                                            setError(null);
                                            setDeleteBlocked(null);
                                            setPendingDelete(m);
                                        }}
                                    >
                                        <Trash2 className="w-3.5 h-3.5" /> Remove
                                    </Button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </CardBody>
            {showAdd && <AddCardDialog onClose={() => setShowAdd(false)} />}
            <RemoveCardConfirmDialog
                method={pendingDelete}
                blocked={deleteBlocked}
                pending={deletePM.isPending}
                onClose={() => {
                    setPendingDelete(null);
                    setDeleteBlocked(null);
                }}
                onConfirm={() => {
                    if (!pendingDelete) return;
                    setError(null);
                    deletePM.mutate(pendingDelete.id, {
                        onSuccess: () => {
                            setPendingDelete(null);
                            setDeleteBlocked(null);
                        },
                        onError: (err) => {
                            // The 409 response body shape is
                            // `{ error, code: 'autopay_active',
                            // affectedInvoices: [{id, displayId}] }`.
                            // The Axios error nests it at err.response
                            // .data. extractErrorMessage flattens to a
                            // string + drops affectedInvoices on the
                            // floor; reach in directly so the dialog
                            // can name the invoices.
                            const data = (err as { response?: { data?: { affectedInvoices?: Array<{ displayId: string }> } } })?.response?.data;
                            const affected = data?.affectedInvoices;
                            if (Array.isArray(affected) && affected.length > 0) {
                                setDeleteBlocked({
                                    displayIds: affected.map((i) => i.displayId).filter(Boolean),
                                });
                            } else {
                                setError(extractErrorMessage(err, 'Could not delete card.'));
                                setPendingDelete(null);
                            }
                        },
                    });
                }}
            />
        </Card>
    );
}

function RemoveCardConfirmDialog({
    method, blocked, pending, onClose, onConfirm,
}: {
    method: PortalPaymentMethod | null;
    blocked: { displayIds: string[] } | null;
    pending: boolean;
    onClose: () => void;
    onConfirm: () => void;
}) {
    // Two display modes:
    //   - Initial confirmation: "are you sure?" with Remove button.
    //   - Blocked: the server 409'd with affected autopay invoices —
    //     swap the dialog body to name them + replace the Remove
    //     button with "Got it" since there's nothing to confirm.
    if (!method) return null;
    const label = method.brand
        ? `${method.brand} ····${method.last4 ?? ''}`
        : `Saved method ····${method.last4 ?? ''}`;
    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>
                        {blocked ? 'Cannot remove this card yet' : `Remove ${label}?`}
                    </DialogTitle>
                </DialogHeader>
                {blocked ? (
                    <div className="text-sm text-text-muted space-y-2">
                        <p>
                            {label} is the default payment method for{' '}
                            {blocked.displayIds.length === 1 ? 'invoice' : 'invoices'}{' '}
                            <span className="font-medium text-text">{blocked.displayIds.join(', ')}</span>,{' '}
                            which {blocked.displayIds.length === 1 ? 'has' : 'have'} autopay turned on.
                        </p>
                        <p>
                            Turn autopay off on{' '}
                            {blocked.displayIds.length === 1 ? 'that invoice' : 'those invoices'} first,
                            or save a different card and make it the default.
                        </p>
                    </div>
                ) : (
                    <p className="text-sm text-text-muted">
                        Stripe will detach this card. You can save it again any time, but you'll have
                        to re-enter the card number.
                    </p>
                )}
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button" disabled={pending}>
                            {blocked ? 'Got it' : 'Cancel'}
                        </Button>
                    </DialogClose>
                    {!blocked && (
                        <Button
                            variant="danger"
                            size="sm"
                            disabled={pending}
                            onClick={onConfirm}
                        >
                            {pending ? 'Removing…' : 'Remove card'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ── Small helpers ───────────────────────────────────────────────────────────
function SectionLoader() {
    return (
        <div className="py-8 flex items-center justify-center text-text-subtle text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
        </div>
    );
}

function formatTime(h: number, m: number): string {
    const hh = ((h % 12) || 12);
    const mm = String(m).padStart(2, '0');
    const ampm = h < 12 ? 'am' : 'pm';
    return `${hh}:${mm} ${ampm}`;
}

function formatDateLong(iso: string | null): string {
    if (!iso) return '—';
    // Parse without timezone shift — the server hands back a YYYY-MM-DD string.
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return iso;
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric', weekday: 'short',
    });
}

// Compact M/D/YYYY for places where the weekday adds noise — the
// invoice-row card in the list view and the invoice-title subtitle on
// the detail page. Dense detail blocks (DOS line items, "Delivered on
// …" footer) still use formatDateLong since the weekday genuinely
// helps the parent orient there.
function formatDateShort(iso: string | null): string {
    if (!iso) return '—';
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return iso;
    return `${m}/${d}/${y}`;
}

// ── Parent signature dialogs ────────────────────────────────────────────────

function CancelSessionDialog({
    session, onClose,
}: { session: PortalSession; onClose: () => void }) {
    const { data: reasons } = useCancelReasons();
    const cancel = useCancelSession();
    const [reasonId, setReasonId] = useState('');
    const [error, setError] = useState<string | null>(null);

    const activeReasons = (reasons ?? []).filter((r) => r.active);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        cancel.mutate(
            { id: session.id, cancelReasonId: reasonId || undefined },
            {
                onSuccess: () => onClose(),
                onError: (err) => {
                    setError(extractErrorMessage(err, 'Unable to cancel right now.'));
                },
            },
        );
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Cancel this session?</DialogTitle>
                    <DialogDescription>
                        The session will be cancelled immediately and your clinician
                        will be notified.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div className="mb-4 p-3 bg-surface-muted border border-border rounded-md text-sm">
                        <div className="font-medium text-text">
                            {formatDateLong(session.date)} · {formatTime(session.startH, session.startM)}
                        </div>
                        <div className="text-xs text-text-subtle mt-0.5">
                            {session.label || 'Session'}
                            {session.staffName ? ` · ${session.staffName}` : ''}
                        </div>
                    </div>

                    {activeReasons.length > 0 && (
                        <FormField label="Reason (optional)">
                            {(id) => (
                                <SelectField
                                    id={id}
                                    value={reasonId}
                                    onChange={(e) => setReasonId(e.target.value)}
                                >
                                    <option value="">Not specified</option>
                                    {activeReasons.map((r) => (
                                        <option key={r.id} value={r.id}>{r.text}</option>
                                    ))}
                                </SelectField>
                            )}
                        </FormField>
                    )}

                    {error && <Alert variant="danger" className="mt-3">{error}</Alert>}

                    <DialogFooter>
                        <DialogClose asChild>
                            <Button type="button" variant="ghost" size="md">
                                Keep session
                            </Button>
                        </DialogClose>
                        <Button
                            type="submit"
                            variant="danger"
                            size="md"
                            disabled={cancel.isPending}
                        >
                            {cancel.isPending ? 'Cancelling…' : 'Cancel session'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function SignPlanDialog({
    plan, clientId, onClose,
}: {
    plan: PortalPendingPlan;
    clientId: string | undefined;
    onClose: () => void;
}) {
    const sign = useSignPortalPlan();
    const { data: relationshipOptions = [] } = useActiveParentRelationships();
    const sigRef = useRef<SignatureCanvasHandle | null>(null);
    const [signerName, setSignerName] = useState('');
    const [relationship, setRelationship] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    // clientId is unused here (parent JWT auto-scopes), but the staff-preview
    // path may still pass it through — keeping the prop for symmetry.
    void clientId;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        const name = signerName.trim();
        if (!name) {
            setError('Please type your full name.');
            return;
        }
        if (!relationship) {
            setError('Please choose your relationship to the client.');
            return;
        }
        const dataUrl = sigRef.current?.toDataUrl() ?? '';
        if (!dataUrl || sigRef.current?.isBlank()) {
            setError('Please draw your signature.');
            return;
        }
        sign.mutate(
            {
                id: plan.id,
                payload: { signerName: name, relationship, signatureImage: dataUrl },
            },
            {
                onSuccess: () => onClose(),
                onError: (err) => {
                    setError(extractErrorMessage(err, 'Unable to save signature.'));
                },
            },
        );
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="w-[min(calc(100vw-2rem),40rem)]">
                <DialogHeader>
                    <DialogTitle>Review &amp; sign treatment plan</DialogTitle>
                    <DialogDescription>
                        Your signature confirms you've reviewed the plan with your clinician.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div className="mb-4 p-3 bg-surface-muted border border-border rounded-md max-h-[28rem] overflow-y-auto space-y-3">
                        <div>
                            <div className="font-medium text-text text-sm">{plan.title}</div>
                            <div className="text-xs text-text-subtle mt-0.5">
                                Sent {formatDateLong(plan.sentToPortalAt?.slice(0, 10) ?? null)}
                                {plan.bcbaName ? ` · BCBA: ${plan.bcbaName}` : ''}
                            </div>
                        </div>

                        {(plan.startDate || plan.reviewDate) && (
                            <div className="grid grid-cols-2 gap-3 text-xs">
                                <div>
                                    <div className="text-text-subtle">Start date</div>
                                    <div className="text-text">
                                        {plan.startDate ? formatDateLong(plan.startDate) : '—'}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-text-subtle">Review date</div>
                                    <div className="text-text">
                                        {plan.reviewDate ? formatDateLong(plan.reviewDate) : '—'}
                                    </div>
                                </div>
                            </div>
                        )}

                        <PlanContent
                            notes={plan.notes}
                            behaviors={plan.behaviors}
                            programs={plan.programs}
                            customFields={plan.customFields}
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <FormField label="Your name" required>
                            {(id) => (
                                <Input
                                    id={id}
                                    value={signerName}
                                    onChange={(e) => setSignerName(e.target.value)}
                                />
                            )}
                        </FormField>
                        <FormField label="Relationship" required>
                            {(id) => (
                                <SelectField
                                    id={id}
                                    value={relationship}
                                    onChange={(e) => setRelationship(e.target.value)}
                                >
                                    <option value="">Select…</option>
                                    {relationshipOptions.map((r) => (
                                        <option key={r.id} value={r.label}>{r.label}</option>
                                    ))}
                                </SelectField>
                            )}
                        </FormField>
                    </div>

                    <div className="mt-3">
                        <div className="flex items-center justify-between mb-1">
                            <div className="text-xs font-medium text-text-subtle">Signature</div>
                            <button
                                type="button"
                                className="text-[11px] text-text-muted hover:text-text underline-offset-2 hover:underline"
                                onClick={() => sigRef.current?.clear()}
                            >
                                Clear
                            </button>
                        </div>
                        <SignatureCanvas ref={sigRef} />
                    </div>

                    {error && <Alert variant="danger" className="mt-3">{error}</Alert>}

                    <DialogFooter>
                        <DialogClose asChild>
                            <Button type="button" variant="ghost" size="md">Cancel</Button>
                        </DialogClose>
                        <Button
                            type="submit"
                            variant="success"
                            size="md"
                            disabled={sign.isPending}
                        >
                            {sign.isPending ? 'Saving…' : 'Sign plan'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function SignNoteDialog({
    session, clientId, onClose,
}: {
    session: PortalSession;
    clientId: string | undefined;
    onClose: () => void;
}) {
    const { data: note, isLoading } = usePortalSessionNote(session.id, clientId);
    const sign = useSignSessionNote();
    const { data: relationshipOptions = [] } = useActiveParentRelationships();
    const sigRef = useRef<SignatureCanvasHandle | null>(null);
    const [signerName, setSignerName] = useState('');
    const [relationship, setRelationship] = useState<string>('');
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        const name = signerName.trim();
        if (!name) {
            setError('Please type your full name.');
            return;
        }
        if (!relationship) {
            setError('Please choose your relationship to the client.');
            return;
        }
        const dataUrl = sigRef.current?.toDataUrl() ?? '';
        if (!dataUrl || sigRef.current?.isBlank()) {
            setError('Please draw your signature.');
            return;
        }
        sign.mutate(
            {
                id: session.id,
                payload: { signerName: name, relationship, signatureImage: dataUrl },
            },
            {
                onSuccess: () => onClose(),
                onError: (err) => {
                    setError(extractErrorMessage(err, 'Unable to save signature.'));
                },
            },
        );
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="w-[min(calc(100vw-2rem),44rem)]">
                <DialogHeader>
                    <DialogTitle>Review &amp; sign session note</DialogTitle>
                    <DialogDescription>
                        {formatDateLong(session.date)} · {formatTime(session.startH, session.startM)}
                        {session.staffName ? ` · ${session.staffName}` : ''}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div className="mb-4 p-3 bg-surface-muted border border-border rounded-md max-h-72 overflow-y-auto">
                        {isLoading ? (
                            <div className="text-text-subtle flex items-center gap-2 text-sm">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                Loading note…
                            </div>
                        ) : (
                            <NoteReadOnlyView
                                noteType={note?.noteType ?? null}
                                noteData={note?.noteData ?? null}
                                sessionNotes={note?.notes}
                            />
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <FormField label="Your name" required>
                            {(id) => (
                                <Input
                                    id={id}
                                    value={signerName}
                                    onChange={(e) => setSignerName(e.target.value)}
                                />
                            )}
                        </FormField>
                        <FormField label="Relationship" required>
                            {(id) => (
                                <SelectField
                                    id={id}
                                    value={relationship}
                                    onChange={(e) => setRelationship(e.target.value)}
                                >
                                    <option value="">Select…</option>
                                    {relationshipOptions.map((r) => (
                                        <option key={r.id} value={r.label}>{r.label}</option>
                                    ))}
                                </SelectField>
                            )}
                        </FormField>
                    </div>

                    <div className="mt-3">
                        <div className="flex items-center justify-between mb-1">
                            <div className="text-xs font-medium text-text-subtle">Signature</div>
                            <button
                                type="button"
                                className="text-[11px] text-text-muted hover:text-text underline-offset-2 hover:underline"
                                onClick={() => sigRef.current?.clear()}
                            >
                                Clear
                            </button>
                        </div>
                        <SignatureCanvas ref={sigRef} />
                    </div>

                    {error && <Alert variant="danger" className="mt-3">{error}</Alert>}

                    <DialogFooter>
                        <DialogClose asChild>
                            <Button type="button" variant="ghost" size="md">Cancel</Button>
                        </DialogClose>
                        <Button
                            type="submit"
                            variant="success"
                            size="md"
                            disabled={sign.isPending}
                        >
                            {sign.isPending ? 'Saving…' : 'Sign note'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function ViewNoteDialog({
    session, clientId, onClose,
}: {
    session: PortalSession;
    clientId: string | undefined;
    onClose: () => void;
}) {
    const { data: note, isLoading } = usePortalSessionNote(session.id, clientId);

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="w-[min(calc(100vw-2rem),44rem)]">
                <DialogHeader>
                    <DialogTitle>Session note</DialogTitle>
                    <DialogDescription>
                        {formatDateLong(session.date)} · {formatTime(session.startH, session.startM)}
                        {session.staffName ? ` · ${session.staffName}` : ''}
                    </DialogDescription>
                </DialogHeader>

                <div className="p-3 bg-surface-muted border border-border rounded-md max-h-[28rem] overflow-y-auto">
                    {isLoading ? (
                        <div className="text-text-subtle flex items-center gap-2 text-sm">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Loading note…
                        </div>
                    ) : (
                        <NoteReadOnlyView
                            noteType={note?.noteType ?? null}
                            noteData={note?.noteData ?? null}
                            sessionNotes={note?.notes}
                        />
                    )}
                </div>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button type="button" variant="ghost" size="md">Close</Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

